/* @jsx h */
import { defineComponent, h } from "vue";
import { CartPersona, CartItem, FoodType, Product } from "@lemmonhq/types";
import { CartAPI, formatPrice } from "@lemmonhq/orders";
import { cn, useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "../../providers/Auth";
import { useShop } from "../../providers/ShopConnection";

interface CartItemsProps {
  items: CartItem[];
  onIncrement: (id: string) => void;
  onDecrement: (id: string) => void;
  cartAPI: CartAPI;
  customers: CartPersona[];
  foodTypes: FoodType[];
  products: Product[];
}

export const CartItems = defineComponent<CartItemsProps>({
  name: "CartItems",
  props: [
    "items",
    "onIncrement",
    "onDecrement",
    "customers",
    "cartAPI",
    "foodTypes",
    "products",
  ],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    const shop = useShop();
    const tableNameOf = (tableId: number) =>
      shop.tables.value.find((ts) => ts.id === tableId)?.table.name ??
      `#${tableId}`;
    const renderItem = (item: CartItem, index: number) => {
      return (
        <div key={`${item.guid}-${index}`} class="py-1">
          <div class="flex items-center justify-between text-sm">
            <div class="flex-1">
              <h3 class="font-medium pr-3 text-accent">{item.name}</h3>
              <p class="">
                {formatPrice(
                  item.price,
                  true,
                  2,
                  auth.user.value?.restaurant.currency || "CHF",
                )}
              </p>
              {item.tableCreditTableId ? (
                <p class="flex items-center gap-1 text-xs text-base-content/70">
                  <span class="material-symbols-outlined text-sm">
                    table_restaurant
                  </span>
                  {t("pos.gift_card.for_table").replace(
                    "{0}",
                    tableNameOf(item.tableCreditTableId),
                  )}
                </p>
              ) : null}
              {item.note && <p class="text-sm  italic">Note: {item.note}</p>}
            </div>
            <div class="flex items-center gap-2 text-sm">
              <button
                onClick={() => props.onDecrement(item.guid)}
                class="p-0 btn btn-sm btn-neutral w-7 h-7 min-h-0 flex justify-center items-center"
              >
                <span class="material-symbols-outlined text-md">remove</span>
              </button>
              <span class="w-7 h-7 bg-white text-black text-center border rounded flex justify-center items-center">
                {item.quantity}
              </span>
              <button
                onClick={() => props.onIncrement(item.guid)}
                class="p-0 btn btn-sm btn-neutral w-7 h-7 min-h-0 flex justify-center items-center"
              >
                <span class="material-symbols-outlined text-sm">add</span>
              </button>
            </div>
          </div>
          {item.selectedBundles && (
            <div class="mt-1 ml-2">
              {(item.selectedBundles || []).map((bundle, bundleIndex) => (
                <div key={bundleIndex} class="text-sm ">
                  <ul class="ml-0">
                    {bundle.options.map((option, optionIndex) => (
                      <li key={optionIndex}>
                        {option.title}
                        {option.price > 0 &&
                          ` (+${option.price.toFixed(2)} ${auth.user.value?.restaurant.currency || "CHF"})`}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };
    const renderItems = (items: CartItem[], showFoodTypes: boolean) => {
      //console.log("renderItems", items, showFoodTypes);
      if (!showFoodTypes) {
        return items.map((item, index) => renderItem(item, index));
      }

      // Group items by food_type_id
      const grouped: Record<string, CartItem[]> = {};

      for (const item of items) {
        const product = props.products.find((el) => item.productId === el.id);
        if (!product) continue;
        const typeId = product.food_type_id || "";
        if (!grouped[typeId]) grouped[typeId] = [];
        grouped[typeId].push(item);
      }
      //console.log("grouped", grouped);

      // Render grouped items
      return Object.entries(grouped).map(([typeId, groupItems]) => {
        const foodType = props.foodTypes.find(
          (ft) => ft.id === parseInt(typeId),
        );
        return (
          <div key={typeId}>
            <h4 class=" uppercase  flex justify-start items-center space-x-1">
              <span class="material-symbols-outlined text-1xl">
                room_service
              </span>
              <span>{foodType?.name || "Unassigned"}</span>
            </h4>
            <div class="space-y-4">
              {groupItems.map((item, index) => renderItem(item, index))}
            </div>
          </div>
        );
      });
    };
    const hiddenClass =
      import.meta.env.VITE_ENV === "production" ? "hidden" : "";
    return () => {
      let showFoodTypes = props.cartAPI.showFoodTypes.value;
      showFoodTypes = false;
      //console.log("foodTypes", props.foodTypes);
      if (
        props.cartAPI.cart.customers.length === 0 &&
        props.items.length == 0
      ) {
        return (
          <div class="flex flex-col text-center justify-center items-center flex-1 gap-2 max-w-72 mx-auto">
            <div class="text-2xl text-accent font-bold">
              {t("pos.cart.empty_title")}
            </div>
            <div class="text-xl">{t("pos.cart.empty_description")}</div>
          </div>
        );
      }

      return (
        <div class="space-y-2">
          <div class="space-y-2 p-2">
            {renderItems(
              props.items.filter((el) => !el.customerId),
              showFoodTypes,
            )}
          </div>
          <div
            class={cn(
              "flex flex-col space-y-4",
              import.meta.env.VITE_ENV === "production" ? "hidden" : "",
            )}
          >
            {props.cartAPI.cart.customers.map((customer, index) => {
              return (
                <div
                  onClick={() => {
                    if (props.cartAPI.activeCustomerId.value === customer.id) {
                      props.cartAPI.activeCustomerId.value = undefined;
                    } else {
                      props.cartAPI.activeCustomerId.value = customer.id;
                    }
                  }}
                  class={
                    `p-2 rounded rounded-xl shadow shadow-xl border space-y-1 ` +
                    (props.cartAPI.activeCustomerId.value === customer.id
                      ? "bg-base-100/50"
                      : "")
                  }
                  key={customer.id}
                >
                  <div class="py-1 flex justify-start items-center space-x-1">
                    {" "}
                    <span class="material-symbols-outlined text-1xl">face</span>
                    <span>{customer.name}</span>
                  </div>
                  {renderItems(
                    props.items.filter((el) => el.customerId === customer.id),
                    showFoodTypes,
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    };
  },
});
